import React, { useState, useEffect } from 'react';
import {
  Trash2, RefreshCw, Database, AlertTriangle, CheckCircle,
  Download, FileText, Users, Star, Loader2, XCircle
} from 'lucide-react';
import { useAdminReset, DataStats } from '../hooks/useAdminReset';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface AdminDataManagerProps {
  onClose: () => void;
}

export function AdminDataManager({ onClose }: AdminDataManagerProps) {
  const {
    loading,
    error,
    getStats,
    directDeleteContracts
  } = useAdminReset();

  const [stats, setStats] = useState<DataStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState('');
  const [deleteResult, setDeleteResult] = useState<{ success: boolean; count: number } | null>(null);
  const [importingFromSam, setImportingFromSam] = useState(false);

  // Load stats on mount
  useEffect(() => {
    async function loadStats() {
      setLoadingStats(true);
      const data = await getStats();
      setStats(data);
      setLoadingStats(false);
    }
    loadStats();
  }, [getStats]);

  const handleDeleteAll = async () => {
    if (confirmDelete !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    toast.loading('Deleting all data...', { id: 'delete' });

    const result = await directDeleteContracts();

    if (result.success) {
      toast.success(`Deleted ${result.count} contracts and all related data`, { id: 'delete' });
      setDeleteResult(result);
      // Refresh stats
      const newStats = await getStats();
      setStats(newStats);
      setConfirmDelete('');
    } else {
      toast.error(result.error || 'Delete failed', { id: 'delete' });
    }
  };

  const handleImportFromSam = async () => {
    setImportingFromSam(true);
    toast.loading('Starting SAM.gov import...', { id: 'import' });

    try {
      // Get user's profile to find their NAICS codes
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('naics_codes')
        .eq('id', user.id)
        .single();

      const naicsCodes = profile?.naics_codes || [];

      if (naicsCodes.length === 0) {
        toast.error('No NAICS codes in your profile. Please add NAICS codes first.', { id: 'import' });
        setImportingFromSam(false);
        return;
      }

      // Call the batch import function with correct parameters
      console.log('🚀 AdminDataManager: Invoking batch-import-sam with NAICS codes:', naicsCodes);
      toast.loading('Calling SAM.gov API - this may take up to 30 seconds...', { id: 'import' });

      const { data, error: fnError } = await supabase.functions.invoke('batch-import-sam', {
        body: {
          naicsCodes: naicsCodes,
          maxRecords: 500,
          batchSize: 300,
          onlyActive: true
        }
      });

      console.log('📡 Edge function response:', { data, error: fnError });

      if (fnError) {
        console.error('❌ Edge function error:', fnError);
        throw fnError;
      }

      if (!data) {
        throw new Error('Edge function returned no data - it may not be deployed. Run: supabase functions deploy batch-import-sam');
      }

      // Check for import result fields
      const imported = data.importedRecords || data.imported || 0;
      const skipped = data.skippedRecords || data.skipped || 0;

      toast.success(
        `Import complete! ${imported} contracts imported, ${skipped} skipped.`,
        { id: 'import' }
      );

      // Refresh stats
      const newStats = await getStats();
      setStats(newStats);

    } catch (e) {
      console.error('Import error:', e);
      const message = e instanceof Error ? e.message : 'Import failed';
      // Check for common issues
      if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
        toast.error('Network error - check that the edge function is deployed', { id: 'import' });
      } else {
        toast.error(message, { id: 'import' });
      }
    } finally {
      setImportingFromSam(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-bw-surface rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-bw-surface border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Database className="h-6 w-6 text-bw-primary mr-3" />
            <h2 className="text-xl font-bold text-bw-ink">Admin Data Manager</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-bw-muted hover:text-bw-muted hover:bg-bw-surface-subtle"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Stats */}
          <div>
            <h3 className="text-lg font-semibold text-bw-ink mb-3">Current Data</h3>
            {loadingStats ? (
              <div className="flex items-center text-bw-muted">
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Loading statistics...
              </div>
            ) : stats ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-bw-primary-tint rounded-lg p-4 text-center">
                  <FileText className="h-8 w-8 text-bw-primary mx-auto mb-2" />
                  <div className="text-2xl font-bold text-blue-900">{stats.contracts}</div>
                  <div className="text-sm text-bw-primary">Contracts</div>
                </div>
                <div className="bg-bw-primary-tint rounded-lg p-4 text-center">
                  <FileText className="h-8 w-8 text-bw-primary mx-auto mb-2" />
                  <div className="text-2xl font-bold text-purple-900">{stats.proposalTemplates}</div>
                  <div className="text-sm text-bw-primary">Proposals</div>
                </div>
                <div className="bg-fit-moderate-bg rounded-lg p-4 text-center">
                  <Star className="h-8 w-8 text-fit-moderate mx-auto mb-2" />
                  <div className="text-2xl font-bold text-amber-900">{stats.favorites}</div>
                  <div className="text-sm text-fit-moderate">Favorites</div>
                </div>
                <div className="bg-success-bg rounded-lg p-4 text-center">
                  <Users className="h-8 w-8 text-success mx-auto mb-2" />
                  <div className="text-2xl font-bold text-green-900">{stats.users}</div>
                  <div className="text-sm text-success">Users</div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Import from SAM.gov */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-bw-ink mb-3">Import Fresh Data</h3>
            <p className="text-sm text-bw-muted mb-4">
              Import new contracts from SAM.gov based on the NAICS codes in your profile.
              This will add contracts from the last 90 days.
            </p>
            <button
              onClick={handleImportFromSam}
              disabled={importingFromSam || loading}
              className="inline-flex items-center px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {importingFromSam ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-2" />
                  Import from SAM.gov
                </>
              )}
            </button>
          </div>

          {/* Delete All Data */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-urgent mb-3 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Danger Zone
            </h3>
            <div className="bg-urgent-bg border border-urgent-border rounded-lg p-4">
              <p className="text-sm text-urgent mb-4">
                <strong>Warning:</strong> This will permanently delete ALL contracts, proposals,
                favorites, and document analysis data across ALL users. User profiles will be preserved.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={confirmDelete}
                  onChange={(e) => setConfirmDelete(e.target.value)}
                  placeholder='Type "DELETE" to confirm'
                  className="flex-1 px-3 py-2 border border-red-300 rounded-md focus:ring-red-500 focus:border-red-500"
                />
                <button
                  onClick={handleDeleteAll}
                  disabled={loading || confirmDelete !== 'DELETE'}
                  className="inline-flex items-center px-4 py-2 bg-urgent text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-5 w-5 mr-2" />
                      Delete All Data
                    </>
                  )}
                </button>
              </div>

              {deleteResult && (
                <div className={`mt-4 p-3 rounded-md ${
                  deleteResult.success ? 'bg-success-bg text-success' : 'bg-urgent-bg text-urgent'
                }`}>
                  {deleteResult.success ? (
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Successfully deleted {deleteResult.count} contracts and all related data.
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <XCircle className="h-5 w-5 mr-2" />
                      Delete failed. Please try again.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Refresh Stats */}
          <div className="border-t pt-6 flex justify-end">
            <button
              onClick={async () => {
                setLoadingStats(true);
                const newStats = await getStats();
                setStats(newStats);
                setLoadingStats(false);
              }}
              disabled={loadingStats}
              className="inline-flex items-center px-4 py-2 border border-bw-border rounded-md hover:bg-bw-surface-subtle"
            >
              <RefreshCw className={`h-5 w-5 mr-2 ${loadingStats ? 'animate-spin' : ''}`} />
              Refresh Stats
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-urgent-bg text-urgent p-4 rounded-lg">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
